import { Collapse, Space, Tag, Typography } from 'antd';
import type { Question, QuestionVersion } from '@titularis/api-client';

/**
 * O que mudou entre a versão vigente e a que ainda não foi publicada.
 *
 * **A cadeia de versões já entrega isto de graça (D-17), e ninguém lia.** A tela
 * avisava "há uma versão salva depois da última publicação" e parava aí: para
 * saber o que mudou, o revisor abria a questão inteira e comparava de memória
 * com o que lembrava do ar. Numa sugestão da máquina que reescreveu três abas e
 * cinco itens, isso é impossível de fazer com confiança.
 *
 * **Só o que mudou aparece.** Campo igual não vira linha - uma lista de doze
 * campos com dez "sem alteração" esconde os dois que importam, que é o mesmo
 * princípio do painel de citações.
 */

type Versao = QuestionVersion;

/** O texto puro de um bloco de HTML, para comparar conteúdo e não marcação. */
function texto(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface Mudanca {
  campo: string;
  antes: string;
  depois: string;
}

const ABA_LABEL: Record<string, string> = {
  legislation: 'Legislação',
  jurisprudence: 'Jurisprudência',
  doctrine: 'Doutrina',
};

/**
 * Compara campo a campo, e não a versão inteira.
 *
 * Comparar o `contentHash` diria apenas "mudou", que é o que a tela já dizia. O
 * que o revisor precisa é saber **onde** olhar.
 */
function comparar(vigente: Versao, pendente: Versao): Mudanca[] {
  const mudancas: Mudanca[] = [];

  const conferir = (campo: string, antes: string, depois: string) => {
    if (antes !== depois) mudancas.push({ campo, antes, depois });
  };

  conferir('Enunciado', texto(vigente.statementHtml), texto(pendente.statementHtml));
  conferir(
    'Gabarito',
    (vigente.correctOption ?? '').toUpperCase(),
    (pendente.correctOption ?? '').toUpperCase(),
  );

  const abasVigente = (vigente.commentary ?? {}) as Record<string, { html?: string }>;
  const abasPendente = (pendente.commentary ?? {}) as Record<string, { html?: string }>;
  for (const aba of new Set([...Object.keys(abasVigente), ...Object.keys(abasPendente)])) {
    conferir(
      ABA_LABEL[aba] ?? aba,
      texto(abasVigente[aba]?.html),
      texto(abasPendente[aba]?.html),
    );
  }

  /*
   * Os itens por letra, e não por posição: a alternativa `c` continua sendo a
   * `c` mesmo que outra tenha sido removida antes dela, e comparar por índice
   * marcaria como alteradas todas as que vieram depois.
   */
  const itensVigente = (vigente.options ?? []) as { letter: string; commentaryHtml?: string | null }[];
  const itensPendente = (pendente.options ?? []) as {
    letter: string;
    commentaryHtml?: string | null;
  }[];
  for (const letra of new Set([
    ...itensVigente.map((i) => i.letter),
    ...itensPendente.map((i) => i.letter),
  ])) {
    conferir(
      `Comentário do item ${letra.toUpperCase()}`,
      texto(itensVigente.find((i) => i.letter === letra)?.commentaryHtml),
      texto(itensPendente.find((i) => i.letter === letra)?.commentaryHtml),
    );
  }

  return mudancas;
}

/** Vazio vira palavra, porque "nada" e "espaço em branco" se confundem na tela. */
function Trecho({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Space orientation="vertical" size={0} style={{ width: '100%' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {rotulo}
      </Typography.Text>
      {valor ? (
        <Typography.Paragraph
          style={{ fontSize: 12, marginBottom: 0 }}
          ellipsis={{ rows: 4, expandable: true, symbol: 'ver tudo' }}
        >
          {valor}
        </Typography.Paragraph>
      ) : (
        <Typography.Text type="secondary" italic style={{ fontSize: 12 }}>
          vazio
        </Typography.Text>
      )}
    </Space>
  );
}

export function DiferencaEntreVersoes({ questao }: { questao: Question }) {
  const vigente = questao.currentVersion;
  const pendente = questao.latestVersion;

  // Sem versão vigente não há com o que comparar: a questão nunca foi publicada,
  // e tudo nela é novo.
  if (!vigente || !pendente || vigente.id === pendente.id) return null;

  const mudancas = comparar(vigente, pendente);
  if (mudancas.length === 0) return null;

  return (
    <Collapse
      size="small"
      items={[
        {
          key: 'diff',
          label: (
            /*
             * `wrap` porque o rótulo é a única linha da barra: num aparelho
             * estreito, "8 campos mudaram desde a versão no ar" mais três
             * etiquetas não cabem, e sem quebrar elas vazam para fora da tela em
             * vez de descer.
             */
            <Space size={6} wrap>
              <Typography.Text>
                {mudancas.length} {mudancas.length === 1 ? 'campo mudou' : 'campos mudaram'} desde a
                versão no ar
              </Typography.Text>
              {mudancas.slice(0, 3).map((mudanca) => (
                <Tag key={mudanca.campo} style={{ marginInlineEnd: 0 }}>
                  {mudanca.campo}
                </Tag>
              ))}
              {mudancas.length > 3 ? (
                <Typography.Text type="secondary">+{mudancas.length - 3}</Typography.Text>
              ) : null}
            </Space>
          ),
          children: (
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {mudancas.map((mudanca) => (
                <Space key={mudanca.campo} orientation="vertical" size={4} style={{ width: '100%' }}>
                  <Typography.Text strong>{mudanca.campo}</Typography.Text>
                  <Trecho rotulo="No ar" valor={mudanca.antes} />
                  <Trecho rotulo="Nesta versão" valor={mudanca.depois} />
                </Space>
              ))}
            </Space>
          ),
        },
      ]}
    />
  );
}
